/**
 * Training API Client
 *
 * Handles training job submission, dataset upload, auto-captioning,
 * and cost estimation. Uses raw fetch for multipart endpoints (upload, caption)
 * and apiCall for JSON endpoints (submit, estimate).
 */

import { apiCall, ApiError } from './api-helpers'

/**
 * Captioning and dataset upload are charged to the wallet before any provider work starts, and
 * answer 402 INSUFFICIENT_BALANCE when it cannot cover them. Returned as an ApiError so callers
 * can tell it apart (`isApiError(err, 'INSUFFICIENT_BALANCE')`) from an ordinary failure.
 */
function insufficientBalanceError(action: string): ApiError {
  return new ApiError(
    `Not enough credits to ${action}. Top up your wallet and try again.`,
    402,
    'INSUFFICIENT_BALANCE',
  )
}

function isInsufficientBalance(status: number, body: { code?: string } | null | undefined): boolean {
  return status === 402 || body?.code === 'INSUFFICIENT_BALANCE'
}

// ── Types ─────────────────────────────────────────────────────────────

export interface UploadResult {
  url: string
  size: number
}

export interface TrainingJobResult {
  jobId: string
  falRequestId: string
  status: string
  estimatedCost: number
  walletBalance: number
}

export interface TrainingEstimate {
  modelName: string
  providerCost: number
  totalCost: number
  pricePerUnit: number
  isFlat: boolean
}

export interface UserLora {
  id: string
  name: string
  triggerWord: string | null
  weightsUrl: string
  baseModel: string
  modelType: string
  thumbnailUrl: string | null
  folderId: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  trainingJob: {
    id: string
    endpointId: string
    config: Record<string, unknown>
    cost: string | null
    completedAt: string | null
    createdAt: string
  } | null
}

export interface ListLorasResult {
  loras: UserLora[]
  total: number
}

export interface TrainingJob {
  id: string
  name: string
  endpointId: string
  status: string
  config: Record<string, unknown>
  createdAt: string
  updatedAt: string
  userLora: { id: string; name: string; triggerWord: string | null } | null
}

export interface ListJobsResult {
  jobs: TrainingJob[]
  total: number
}

export interface SubmitJobParams {
  modelEndpointId: string
  name: string
  datasetUrl: string
  config: Record<string, unknown>
  datasetMeta?: {
    imageCount?: number
    totalSizeBytes?: number
    captionCount?: number
    triggerWord?: string
    thumbnailUrl?: string
  }
}

// ── API ───────────────────────────────────────────────────────────────

export const trainingAPI = {
  /**
   * Upload dataset ZIP to fal.ai storage via backend proxy.
   * Uses XMLHttpRequest for upload progress tracking (fetch API doesn't support it).
   */
  async uploadDataset(
    zipBlob: Blob,
    filename: string,
    onProgress?: (pct: number) => void,
  ): Promise<UploadResult> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
    if (!apiBaseUrl) throw new Error('VITE_API_BASE_URL not configured')

    const formData = new FormData()
    formData.append('file', zipBlob, filename)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${apiBaseUrl}/api/training/upload`)
      xhr.withCredentials = true

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100)
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText)
            if (json.success) {
              resolve(json.data)
            } else {
              reject(new Error(json.error || 'Upload failed'))
            }
          } catch {
            reject(new Error('Invalid response from upload'))
          }
        } else {
          let json: { error?: string; code?: string } | null = null
          try {
            json = JSON.parse(xhr.responseText)
          } catch {
            // Non-JSON error body — fall through to the status-based message.
          }
          if (isInsufficientBalance(xhr.status, json)) {
            reject(insufficientBalanceError('upload this dataset'))
          } else {
            reject(new Error(json?.error || `Upload failed: ${xhr.status}`))
          }
        }
      }

      xhr.onerror = () => reject(new Error('Upload network error'))
      xhr.ontimeout = () => reject(new Error('Upload timed out'))
      xhr.timeout = 120_000 // 2 min timeout for large ZIPs

      xhr.send(formData)
    })
  },

  /**
   * Caption a single image using Florence-2 via backend proxy.
   * Uses raw fetch (not apiCall) because multipart needs auto Content-Type.
   */
  async captionImage(file: File): Promise<string> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
    if (!apiBaseUrl) throw new Error('VITE_API_BASE_URL not configured')

    const formData = new FormData()
    formData.append('image', file)

    const res = await fetch(`${apiBaseUrl}/api/training/caption`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string; code?: string }
      if (isInsufficientBalance(res.status, data)) throw insufficientBalanceError('caption this image')
      throw new Error(data.error || `Caption failed: ${res.status}`)
    }

    const json = await res.json() as { success: boolean; data: { caption: string } }
    return json.data.caption
  },

  /**
   * Submit a training job to the backend.
   */
  async submitJob(params: SubmitJobParams): Promise<TrainingJobResult> {
    const result = await apiCall('/api/training/jobs', true, {
      method: 'POST',
      body: JSON.stringify(params),
    }) as { success: boolean; data: TrainingJobResult }
    return result.data
  },

  /**
   * List user's LoRA models.
   */
  async listLoras(type: 'image' | 'video' | 'all' = 'all'): Promise<ListLorasResult> {
    const result = await apiCall(
      `/api/training/loras?type=${type}&limit=50&offset=0`,
      true,
    ) as { success: boolean; data: ListLorasResult }
    return result.data
  },

  /**
   * List user's training jobs (includes in-progress ones).
   */
  async listJobs(): Promise<ListJobsResult> {
    const result = await apiCall(
      '/api/training/jobs?limit=50&offset=0',
      true,
    ) as { success: boolean; data: ListJobsResult }
    return result.data
  },

  /**
   * Rename a LoRA model.
   */
  async renameLora(id: string, name: string): Promise<{ id: string; name: string }> {
    const result = await apiCall(`/api/training/loras/${id}`, true, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }) as { success: boolean; data: { id: string; name: string } }
    return result.data
  },

  /**
   * Delete (archive) a LoRA model.
   */
  async deleteLora(id: string): Promise<{ id: string; isActive: boolean }> {
    const result = await apiCall(`/api/training/loras/${id}`, true, {
      method: 'DELETE',
    }) as { success: boolean; data: { id: string; isActive: boolean } }
    return result.data
  },

  /**
   * Dismiss (soft-delete) a failed training job.
   */
  async deleteTrainingJob(id: string): Promise<{ id: string }> {
    const result = await apiCall(`/api/training/jobs/${id}`, true, {
      method: 'DELETE',
    }) as { success: boolean; data: { id: string } }
    return result.data
  },

  /**
   * Get cost estimate for a training configuration.
   */
  async getEstimate(modelEndpointId: string, steps: number): Promise<TrainingEstimate> {
    const result = await apiCall(
      `/api/training/estimate?modelEndpointId=${encodeURIComponent(modelEndpointId)}&steps=${steps}`,
      true,
    ) as { success: boolean; data: TrainingEstimate }
    return result.data
  },

  /**
   * Move a LoRA to a folder (or root if folderId is null).
   */
  async moveLoraToFolder(id: string, folderId: string | null): Promise<void> {
    await apiCall(`/api/training/loras/${id}/folder`, true, {
      method: 'PUT',
      body: JSON.stringify({ folderId }),
    })
  },
}

// ── LoRA Folder API ──────────────────────────────────────────────────

export interface LoraFolder {
  id: string
  name: string
  loraCount: number
  createdAt: string
  updatedAt: string
}

export const loraFolderAPI = {
  async list(): Promise<LoraFolder[]> {
    const result = await apiCall('/api/lora-folders', true) as { success: boolean; data: LoraFolder[] }
    return result.data
  },

  async create(name: string): Promise<LoraFolder> {
    const result = await apiCall('/api/lora-folders', true, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }) as { success: boolean; data: LoraFolder }
    return result.data
  },

  async rename(id: string, name: string): Promise<{ id: string; name: string }> {
    const result = await apiCall(`/api/lora-folders/${id}`, true, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }) as { success: boolean; data: { id: string; name: string } }
    return result.data
  },

  async remove(id: string): Promise<void> {
    await apiCall(`/api/lora-folders/${id}`, true, {
      method: 'DELETE',
    })
  },
}
